/**
 * AUTHENTICATION STATE FOR THE APP
 * ================================
 * This is the shared place where the app learns:
 * - whether a person is signed in
 * - who they are
 * - how to send them to sign in or sign out
 *
 * Azure Static Web Apps keeps the login session in a browser cookie, so the app does not store
 * or manage its own password or token. It simply asks SWA who the current person is.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  clearStandbyReauthAttempt,
  isStandbyHostname,
  tryStandbyReauth,
} from "@/utils/standbySwaReauth";

type SwaClientPrincipal = {
  userId?: string;
  // Usually the person's email address or sign-in name.
  userDetails?: string;
  identityProvider?: string;
  userRoles?: string[];
  isAdmin?: boolean;
  isActive?: boolean;
};

type EffectiveUserPayload = {
  email?: string | null;
  roles?: string[];
  isAdmin?: boolean;
  isActive?: boolean;
};

type EffectiveUserResult = {
  payload: EffectiveUserPayload | null;
  status: number | null;
};

type AuthContextType = {
  user: SwaClientPrincipal | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Tells the rest of the app that the first sign-in check has finished.
  authReady: boolean;
  refreshUser: () => Promise<void>;
  login: (returnTo?: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const LOCALHOST_HOSTNAMES = new Set([
  "localhost",
  "127.0.0.1",
]);

const LOCAL_DEV_USER: SwaClientPrincipal = {
  userId: "local-dev-user",
  userDetails: "local.admin@linfinityeng.com",
  identityProvider: "local-dev",
  userRoles: ["authenticated", "admin", "pm", "finance"],
  isAdmin: true,
  isActive: true,
};

function normalizeRoles(userRoles?: string[] | null) {
  return Array.from(
    new Set((userRoles || []).map((role) => String(role || '').trim().toLowerCase()).filter(Boolean))
  );
}

function isLocalDevelopmentHost(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return import.meta.env.DEV && LOCALHOST_HOSTNAMES.has(window.location.hostname.toLowerCase());
}

async function fetchEffectiveUser(): Promise<EffectiveUserResult> {
  try {
    const res = await fetch("/api/v1/me", { credentials: "include" });
    if (!res.ok) {
      return { payload: null, status: res.status };
    }

    const data = await res.json();
    const payload = data?.data;
    if (!payload || typeof payload !== "object") {
      return { payload: null, status: res.status };
    }

    return {
      status: res.status,
      payload: {
        email: typeof payload.email === "string" ? payload.email : null,
        roles: Array.isArray(payload.roles) ? normalizeRoles(payload.roles) : [],
        isAdmin: typeof payload.isAdmin === "boolean" ? payload.isAdmin : undefined,
        isActive: typeof payload.isActive === "boolean" ? payload.isActive : undefined,
      },
    };
  } catch (error) {
    console.warn('⚠️ SWA Auth: Failed to fetch effective user profile', error);
    return { payload: null, status: null };
  }
}

// Ask SWA for the current signed-in user.
async function fetchSwaUser(): Promise<SwaClientPrincipal | null> {
  if (isLocalDevelopmentHost()) {
    console.log('🧪 SWA Auth: Using local development auth bypass user');
    return LOCAL_DEV_USER;
  }

  try {
    console.log('🔍 SWA Auth: Fetching user from /.auth/me');
    const res = await fetch("/.auth/me", { credentials: "include" });
    if (!res.ok) {
      console.log('❌ SWA Auth: /.auth/me returned non-OK status:', res.status);
      return null;
    }
    const data = await res.json();
    const principal = data?.clientPrincipal ?? null;
    if (principal) {
      const effectiveUser = await fetchEffectiveUser();
      if (!effectiveUser.payload && (effectiveUser.status === 401 || effectiveUser.status === 404 || effectiveUser.status === 503)) {
        const returnTo = window.location.pathname + window.location.search;
        tryStandbyReauth(returnTo);
      } else if (typeof window !== "undefined" && effectiveUser.payload) {
        clearStandbyReauthAttempt();
      }

      const mergedPrincipal: SwaClientPrincipal = {
        ...principal,
        userDetails: effectiveUser.payload?.email || principal.userDetails,
        userRoles: normalizeRoles([
          ...(principal.userRoles || []),
          ...(effectiveUser.payload?.roles || []),
        ]),
        isAdmin: effectiveUser.payload?.isAdmin,
        isActive: effectiveUser.payload?.isActive,
      };
      console.log('✅ SWA Auth: User authenticated:', mergedPrincipal.userDetails);
      return mergedPrincipal;
    } else {
      console.log('🔍 SWA Auth: No authenticated user');
    }
    return principal;
  } catch (error) {
    console.error('❌ SWA Auth: Error fetching user:', error);
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SwaClientPrincipal | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  const refreshUser = async () => {
    const u = await fetchSwaUser();
    setUser(u);
  };

  useEffect(() => {
    (async () => {
      console.log('🔄 SWA Auth: Initializing...');
      setIsLoading(true);
      await refreshUser();
      setIsLoading(false);
      setAuthReady(true);
      console.log('✅ SWA Auth: Initialization complete');
    })();
  }, []);

  // Send the user to SWA's sign-in page and come back to the same place afterward.
  const login = (returnTo?: string) => {
    const redirect = returnTo ?? window.location.pathname + window.location.search;
    if (isLocalDevelopmentHost()) {
      console.log('🧪 SWA Auth: Local development login shortcut to:', redirect);
      window.location.href = redirect;
      return;
    }
    const loginUrl = "/.auth/login/aad?post_login_redirect_uri=" + encodeURIComponent(redirect);
    console.log('🔑 SWA Auth: Redirecting to login:', loginUrl);
    window.location.href = loginUrl;
  };

  // Send the user to SWA's sign-out page and return them to the home screen.
  const logout = () => {
    if (isLocalDevelopmentHost()) {
      console.log('🧪 SWA Auth: Local development logout shortcut');
      window.location.href = "/";
      return;
    }
    const logoutUrl = "/.auth/logout?post_logout_redirect_uri=" + encodeURIComponent("/");
    console.log('🔓 SWA Auth: Redirecting to logout:', logoutUrl);
    window.location.href = logoutUrl;
  };

  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated: Boolean(user?.userId),
    isLoading,
    authReady,
    refreshUser,
    login,
    logout,
  }), [user, isLoading, authReady]);

  // Debug authentication state changes
  useEffect(() => {
    console.log('🔄 SWA Auth state change:', {
      hasUser: !!user,
      userDetails: user?.userDetails,
      isAuthenticated: Boolean(user?.userId),
      isLoading,
      authReady,
    });
  }, [user, isLoading, authReady]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
